import type { EnvironmentManager } from '@/environment/EnvironmentManager';
import { DAYS_IN_YEAR } from '@/environment/SeasonModel';
import type { DaylightWindow } from '@/environment/SunModel';
import { el, setClass } from './dom';
import { icon, type IconName } from './Icons';
import { formatDayOfYear } from './PlantMetrics';
import { ChronoRibbon, paintDot } from './ChronoRibbon';
import { WHEEL_DAYS, setSceneDate } from './DateScrubber';
import {
  formatMinutes,
  seasonGradientCss,
  seasonMoment,
  skyGradientCss,
  timeMoment,
} from './ChronoPalette';

/**
 * The time-and-date HUD: a floating bar pinned to the top centre of the viewport, above everything
 * else, holding the two controls that change what the whole scene looks like.
 *
 * Why it lives here and not in the environment tray: time of day and date are not settings, they are
 * a *viewpoint*. Every plant colour, every shadow, and every capture depends on them, and they get
 * moved constantly while looking at the model — so they belong in the eye line, over the scene, not
 * three panels deep on the right. Everything else about the environment (latitude, weather) is
 * set once and left alone, and stays in the tray.
 *
 * Two design rules follow from being permanently on screen:
 *
 * 1. **It must not cost anything to have there.** After a few seconds of being left alone the bar
 *    compresses to a small pill — a glyph, the clock, the date — and drops to a low opacity. Hover,
 *    focus, or touch it and it opens again. It is never removed from the DOM or the tab order, so it
 *    is always one Tab away.
 * 2. **It must be readable at a glance, without reading.** The tracks are pictures: the day is a sky
 *    painted from the site's real sunrise and sunset, the year is a landscape turning through the
 *    seasons. The handle carries the colour of the moment it sits on, and the glyph for it — a moon
 *    at midnight, a leaf in October. See {@link ChronoPalette}.
 *
 * The bar owns no state: both handles read and write {@link EnvironmentManager} directly, so a
 * change made anywhere else shows up here on the next refresh, and a change made here reaches the
 * sun, the season, and autosave through the same path as any other environment edit.
 *
 * The handles themselves are {@link ChronoRibbon}, which is also what the flora database's own date
 * scrubber is built from — see {@link DateScrubber}. Two surfaces scrub the calendar and they are
 * the same widget writing the same field, so neither can drift from the other in geometry or in
 * what it does to the scene.
 */

export interface ChronoBarDeps {
  environment: EnvironmentManager;
  /** Called after a drag so the rest of the HUD (thumbnails, inspector) can follow the season. */
  onChange: () => void;
  /** Milliseconds of being left alone before the bar compresses. */
  idleMs?: number;
}

const DEFAULT_IDLE_MS = 3200;

/** Step is 5 minutes, so the last reachable value is 23:55 — not 23:59. */
const MAX_MINUTES = 1435;
const MINUTE_STEP = 5;

/**
 * One wheel notch over the time ribbon. Coarse enough to cross a day, fine enough to land in one.
 * The date's notch is {@link WHEEL_DAYS}, defined beside the scrubber that shares it.
 */
const WHEEL_MINUTES = 15;

export class ChronoBar {
  /** The rail: the card plus the cover switches that hang off it. This is what the HUD mounts. */
  readonly root: HTMLElement;
  /** The card itself — everything CSS keys on `.lg-chrono` (compaction, the sky, its width). */
  private readonly card: HTMLElement;

  private readonly time: ChronoRibbon;
  private readonly date: ChronoRibbon;
  private readonly sunriseTick: HTMLElement;
  private readonly sunsetTick: HTMLElement;
  private readonly timeValue: HTMLElement;
  private readonly timeCaption: HTMLElement;
  private readonly dateValue: HTMLElement;
  private readonly dateCaption: HTMLElement;
  private readonly footSunrise: HTMLElement;
  private readonly footElevation: HTMLElement;
  private readonly footSunset: HTMLElement;
  private readonly pillTimeDot: HTMLElement;
  private readonly pillTime: HTMLElement;
  private readonly pillDateDot: HTMLElement;
  private readonly pillDate: HTMLElement;
  private readonly winter: HTMLElement;
  private readonly snowToggle: HTMLElement;
  private readonly iceToggle: HTMLElement;
  private readonly pillWinter: HTMLElement;

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private pointerInside = false;
  private readonly detach: (() => void)[] = [];

  /** Whether either handle is under the pointer. The ribbons track their own drags — see {@link ChronoRibbon}. */
  private get scrubbing(): boolean {
    return this.time.scrubbing || this.date.scrubbing;
  }

  constructor(private readonly deps: ChronoBarDeps) {
    this.timeValue = el('span', { class: 'lg-chrono__value' });
    this.timeCaption = el('span', { class: 'lg-chrono__caption' });
    this.dateValue = el('span', { class: 'lg-chrono__value' });
    this.dateCaption = el('span', { class: 'lg-chrono__caption' });

    this.sunriseTick = el('span', { class: 'lg-chrono__tick' });
    this.sunsetTick = el('span', { class: 'lg-chrono__tick' });

    this.time = this.ribbon(
      'time',
      'Time of day',
      { min: 0, max: MAX_MINUTES, step: MINUTE_STEP, wheel: WHEEL_MINUTES },
      (value) => {
        this.deps.environment.setTime(Math.floor(value / 60), value % 60);
        this.commit();
      },
    );
    this.time.track.append(this.sunriseTick, this.sunsetTick);

    this.date = this.ribbon(
      'date',
      'Date',
      { min: 1, max: DAYS_IN_YEAR, step: 1, wheel: WHEEL_DAYS },
      (value) => {
        setSceneDate(this.deps.environment, value);
        this.commit();
      },
    );
    this.date.track.style.backgroundImage = seasonGradientCss();

    this.footSunrise = el('span', { class: 'lg-chrono__fact' });
    this.footElevation = el('span', { class: 'lg-chrono__fact' });
    this.footSunset = el('span', { class: 'lg-chrono__fact' });

    this.pillTimeDot = el('span', { class: 'lg-chrono__dot lg-chrono__dot--pill' });
    this.pillTime = el('span', { class: 'lg-chrono__pill-value' });
    this.pillDateDot = el('span', { class: 'lg-chrono__dot lg-chrono__dot--pill' });
    this.pillDate = el('span', { class: 'lg-chrono__pill-value' });
    // Read-only, and only there when something is lying on the ground: the compressed bar has to
    // keep saying what the scene is, and "there is snow on this site" is as much a part of that as
    // the hour. Toggling it is for the open card — hovering opens that instantly.
    this.pillWinter = el('span', { class: 'lg-chrono__pill-winter', attrs: { 'aria-hidden': 'true' } });

    this.snowToggle = this.coverToggle('Snow', 'snowflake', () => {
      const cover = this.deps.environment.state.groundCover;
      this.deps.environment.setGroundCover({ snow: !cover.snow });
      this.commit();
    });
    this.iceToggle = this.coverToggle('Ice', 'ice', () => {
      const cover = this.deps.environment.state.groundCover;
      this.deps.environment.setGroundCover({ ice: !cover.ice });
      this.commit();
    });
    this.winter = el(
      'div',
      { class: 'lg-chrono__winter', attrs: { 'aria-label': 'Winter ground' } },
      this.snowToggle,
      this.iceToggle,
    );

    this.card = el(
      'section',
      { class: 'lg-chrono', attrs: { 'aria-label': 'Time and date' } },
      // The compressed form. Marked aria-hidden because it only restates the sliders' own values.
      el(
        'div',
        { class: 'lg-chrono__pill', attrs: { 'aria-hidden': 'true' } },
        this.pillTimeDot,
        this.pillTime,
        el('span', { class: 'lg-chrono__pill-sep' }),
        this.pillDate,
        this.pillDateDot,
        this.pillWinter,
      ),
      el(
        'div',
        { class: 'lg-chrono__full' },
        el(
          'div',
          { class: 'lg-chrono__heads' },
          el('span', { class: 'lg-chrono__head' }, this.timeValue, this.timeCaption),
          el(
            'span',
            { class: 'lg-chrono__head lg-chrono__head--end' },
            this.dateValue,
            this.dateCaption,
          ),
        ),
        this.time.root,
        this.date.root,
        el('div', { class: 'lg-chrono__foot' }, this.footSunrise, this.footElevation, this.footSunset),
      ),
    );

    /**
     * The card and, hanging off its right edge, the ground-cover switches.
     *
     * The switches are a *sibling* of the card and absolutely positioned, which is the whole point:
     * they were inside it, and the card grew and shrank as the date handle crossed 1 November. That
     * is intolerable in a control you steer by pixel — the track you are dragging changed length
     * under the pointer, so the day under your finger moved. Out here they cost the card no width
     * at all: appearing and disappearing changes nothing about the ribbons, and the date you are
     * scrubbing stays exactly where you put it.
     *
     * Beside the date rather than anywhere else because cover *is* a property of the date: the
     * ground can only be under snow on the days this handle can reach.
     */
    this.root = el('div', { class: 'lg-chrono-rail' }, this.card, this.winter);

    this.bindActivity();
    this.refresh();
    // Starts open so it is discoverable on load, then settles into the pill on its own.
    this.scheduleCompact();
  }

  /** Re-read the environment and repaint. Safe to call every frame: no DOM is rebuilt. */
  refresh(): void {
    const { environment } = this.deps;
    const state = environment.state;
    const day = environment.effectiveDayOfYear();
    const daylight = environment.daylight();
    const minutes = state.time.hour * 60 + state.time.minute;

    /* ------------------------------------- Time ------------------------------------- */
    const moment = timeMoment(minutes, daylight);
    const clock = formatMinutes(minutes);
    const sky = skyGradientCss(daylight);
    this.time.set(minutes, moment, clock);
    this.time.track.style.backgroundImage = sky;
    this.timeValue.textContent = clock;
    this.timeCaption.textContent = moment.label;
    this.placeTick(this.sunriseTick, daylight.sunriseMinutes, 'Sunrise');
    this.placeTick(this.sunsetTick, daylight.sunsetMinutes, 'Sunset');

    /* ------------------------------------- Date ------------------------------------- */
    const season = seasonMoment(day);
    const dateLabel = formatDayOfYear(day);
    this.date.set(day, season, dateLabel);
    this.dateValue.textContent = dateLabel;
    this.dateCaption.textContent = season.label;

    /* ------------------------------------- Facts ------------------------------------ */
    const sun = environment.sunLighting();
    this.footSunrise.textContent = describeHorizon(daylight, 'sunrise');
    this.footSunset.textContent = describeHorizon(daylight, 'sunset');
    this.footElevation.textContent =
      sun.elevationDegrees > 0
        ? `Sun ${Math.round(sun.elevationDegrees)}° · ${Math.round(sun.azimuthDegrees)}° az`
        : `Sun ${Math.round(sun.elevationDegrees)}° · below horizon`;

    /* --------------------------------- Winter ground -------------------------------- */
    // Present only in the months it can be true in. Everything here is an attribute or a class on
    // elements that already exist, so this is safe on every frame of a date drag — the controls
    // appear and vanish as the handle crosses 1 November and 15 March.
    const freezing = environment.isFreezing();
    const cover = state.groundCover;
    this.winter.hidden = !freezing;
    this.setToggle(this.snowToggle, 'Snow', freezing && cover.snow);
    this.setToggle(this.iceToggle, 'Ice', freezing && cover.ice);

    /* ---------------------------------- Compressed ---------------------------------- */
    this.pillTime.textContent = clock;
    this.pillDate.textContent = dateLabel;
    const worn: IconName[] = [];
    if (freezing && cover.snow) worn.push('snowflake');
    if (freezing && cover.ice) worn.push('ice');
    this.pillWinter.hidden = worn.length === 0;
    // The pill is a fixed width and clips; the glyphs need their own room in it.
    setClass(this.card, 'has-cover', worn.length > 0);
    const glyphs = worn.map((name) => icon(name, 12)).join('');
    if (this.pillWinter.innerHTML !== glyphs) this.pillWinter.innerHTML = glyphs;
    paintDot(this.pillTimeDot, moment, 13);
    paintDot(this.pillDateDot, season, 13);
    // The card keeps a sliver of the sky along its top edge, and it is the one part that survives
    // into the pill: even compressed, the bar says roughly what time it is before you read a digit.
    this.card.style.setProperty('--chrono-sky', sky);
  }

  /** Open the bar (or let it settle back down) without waiting for a pointer. */
  setCompact(compact: boolean): void {
    // On the card, not the rail: every compaction rule keys off `.lg-chrono`, and the switches
    // follow it through a sibling selector rather than a second copy of the state.
    setClass(this.card, 'is-compact', compact);
  }

  dispose(): void {
    this.clearTimer();
    for (const off of this.detach) off();
    this.detach.length = 0;
    this.root.remove();
  }

  /* ------------------------------------- Building ---------------------------------- */

  private ribbon(
    kind: 'time' | 'date',
    label: string,
    range: { min: number; max: number; step: number; wheel: number },
    onInput: (value: number) => void,
  ): ChronoRibbon {
    // Taking hold of a handle holds the bar open; letting go restarts the fade clock. The ribbon
    // tracks the drag itself, including the release that lands off the track — see ChronoRibbon.
    const ribbon = new ChronoRibbon({
      kind,
      label,
      ...range,
      onInput,
      onGrab: () => this.expand(),
      onRelease: () => this.scheduleCompact(),
    });
    this.detach.push(() => ribbon.dispose());
    return ribbon;
  }

  /**
   * One cover switch: an icon, a word, and a pressed state.
   *
   * A button rather than the tray's sliding switch. At this size a switch's track would be wider
   * than the label beside it, and the bar's whole discipline is that nothing on it costs more room
   * than what it tells you.
   */
  private coverToggle(label: string, glyph: IconName, onClick: () => void): HTMLElement {
    return el(
      'button',
      {
        class: 'lg-chrono__toggle',
        attrs: { type: 'button' },
        on: { click: onClick },
      },
      el('span', { class: 'lg-chrono__toggle-icon', html: icon(glyph, 14) }),
      el('span', { text: label }),
    );
  }

  private setToggle(button: HTMLElement, label: string, on: boolean): void {
    setClass(button, 'is-active', on);
    button.setAttribute('aria-pressed', String(on));
    button.title = on ? `${label} on — click to clear the ground` : `Lay ${label.toLowerCase()}`;
  }

  private placeTick(tick: HTMLElement, minutes: number | null, label: string): void {
    if (minutes === null || minutes < 0 || minutes > MAX_MINUTES) {
      tick.hidden = true;
      return;
    }
    tick.hidden = false;
    tick.style.setProperty('--t', (minutes / MAX_MINUTES).toFixed(5));
    tick.title = `${label} ${formatMinutes(minutes)}`;
  }

  /* ------------------------------------ Fade logic --------------------------------- */

  private bindActivity(): void {
    const on = <K extends keyof HTMLElementEventMap>(
      type: K,
      handler: (event: HTMLElementEventMap[K]) => void,
    ): void => {
      this.root.addEventListener(type, handler as EventListener);
      this.detach.push(() => this.root.removeEventListener(type, handler as EventListener));
    };

    on('pointerenter', () => {
      this.pointerInside = true;
      this.expand();
    });
    on('pointerleave', () => {
      this.pointerInside = false;
      this.scheduleCompact();
    });
    // Keyboard reaches the bar through the range inputs, which is why neither form is ever removed
    // from the tab order: tabbing in opens it, tabbing out lets it settle.
    on('focusin', () => this.expand());
    on('focusout', () => this.scheduleCompact());
    on('input', () => this.expand());
  }

  private expand(): void {
    this.clearTimer();
    this.setCompact(false);
  }

  /** Settle into the pill once nothing is holding the bar open. */
  private scheduleCompact(): void {
    this.clearTimer();
    if (this.pointerInside || this.scrubbing) return;
    if (this.root.contains(document.activeElement)) return;
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      this.setCompact(true);
    }, this.deps.idleMs ?? DEFAULT_IDLE_MS);
  }

  private clearTimer(): void {
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  /** Repaint locally and tell the HUD, then restart the fade clock. */
  private commit(): void {
    this.refresh();
    this.deps.onChange();
    this.scheduleCompact();
  }
}

function describeHorizon(daylight: DaylightWindow, edge: 'sunrise' | 'sunset'): string {
  if (daylight.polarDay) return edge === 'sunrise' ? 'Midnight sun' : 'No sunset';
  if (daylight.polarNight) return edge === 'sunrise' ? 'Polar night' : 'No sunrise';
  const minutes = edge === 'sunrise' ? daylight.sunriseMinutes : daylight.sunsetMinutes;
  if (minutes === null) return '—';
  return `${edge === 'sunrise' ? 'Sunrise' : 'Sunset'} ${formatMinutes(minutes)}`;
}
